import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ScanLogWorkflow } from 'src/app/app.enums';
import { BasePage } from 'src/app/base.page';
import { BaseService, ScanningService } from 'src/app/services';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import { PatientModel } from 'src/app/models/patient.model';
import { PatientProperty } from 'src/app/utils/patient-info-content.util';
import { OverlayEventDetail } from '@ionic/core/components';
import { ModalController } from '@ionic/angular';
import { MilkBottleModel } from 'src/app/models/milk.model';
import { KioskService } from 'src/app/services/kiosk.service';

@Component({
	selector: 'app-kiosk-label',
	templateUrl: './kiosk-label.component.html',
	styleUrls: ['./kiosk-label.component.scss'],
})
export class KioskLabelComponent extends BasePage implements OnInit {

	@Input() currentPatient: PatientModel[] = [];
	@Input() excludesPatientProperty: PatientProperty[] = [
		PatientProperty.AdmittedDate,
		PatientProperty.BedId,
		PatientProperty.BottlesRemaining,
		PatientProperty.Gender,
		PatientProperty.Siblings,
	];
	@Output() startOverEmitter = new EventEmitter();
	@Output() printedEmitter = new EventEmitter();
	@Output() exitKioskEmitter = new EventEmitter();
	@Output() resetTimerEmitter = new EventEmitter();

	createdMilks: MilkBottleModel[] = [];

	faPlus = faPlus;
	faMinus = faMinus;
	maxAmount = this.StorageService.configs.kioskSettings.maxLabelAmount;
	printer = this.StorageService.lastUsedCachedPrinter;
	numbers: number[] = [5, 10, 15, 20, 30, 50, 75, 100];

	constructor(
		public base: BaseService,
		public kioskService: KioskService,
		public scanningService: ScanningService,
		private modalCtrl: ModalController,
	) {
		super(base, scanningService);
		this.workflow = ScanLogWorkflow.Kiosk;
	}

	public async ngOnInit() {
		this.labelQuantity = 0;
		const filteredNumbers = this.numbers.filter(
			(numbers) => numbers <= this.maxAmount,
		);
		this.numbers = filteredNumbers;
	}

	startOver() {
		this.startOverEmitter.emit();
	}

	exitKiosk() {
		this.exitKioskEmitter.emit();
	}

	async printed() {
		await this.modalCtrl.dismiss(null, 'cancel');
		this.printedEmitter.emit();
	}

	handleTextChanged(value: string) {
		this.labelQuantity = Number.parseInt(value);
	}

	increaseAmount() {
		let amount = this.labelQuantity;
		if (amount < this.maxAmount) {
			amount++;
			this.labelQuantity = amount;
		}
	}

	decreaseAmount() {
		let amount = this.labelQuantity;
		if (amount > 0) {
			amount--;
			this.labelQuantity = amount;
		}
	}

	async cancelPrint() {
		if (this.modalCtrl) {
			await this.modalCtrl.dismiss(null, 'cancel');
		}
	}

	closeModal(): void {
		this.cancelPrint();
	}

	onWillDismiss(event: CustomEvent<OverlayEventDetail>) {
		this.resetTimerEmitter.emit();
	}

	/**
	 * Create fresh milk, update and save scanned products.
	 * If "use all" don't print label
	 *
	 * Fresh milk bottles should have 'now' for their received date.
	 */
	async saveChanges() {
		try {
			await this.base.presentLoading('Saving');

			const createdMilks = await this.kioskService.createNewMilkBottle(
				{
					numberOfBottles: this.labelQuantity,
					mothersId: this.currentPatient[0].motherId,
					mrn: this.currentPatient[0].mrn
				},
			);

			// To provide complete data (siblings, unprepared milk bottle),
			// we need to get additional information about the patient on the
			// created milk bottle. Since all bottles should return the same patient
			// data, we'll get the information once and then assign it.
			const patients =
				await this.kioskService.getKioskPatientDetailsByMilkBottle(
					createdMilks[0],
				);

			this.createdMilks = createdMilks.map((x) => {
				x.patients = patients;
				return x;
			});
		} catch (error) {
			console.error(error);
		} finally {
			await this.base.dismissLoading();
		}
	}
}
